import { useMemo } from 'react'
import { Badge, Flex, Text } from '@radix-ui/themes'
import type { Ops } from '@willie/schemas'
import type { TRPCOutputs } from '~/utils/trpc'
import { SectionCard } from '~/components/SectionCard'
import { daysStalledColor, integerDaysSince } from '~/features/installations/overview'
import { computeDeliveryHealth, computeTimingRows } from '../timeline'
import { DeliveryHealthPanel } from './DeliveryHealthPanel'
import { OrgTicketsCard } from './OrgTicketsCard'

type Installation = TRPCOutputs['ops']['stationInstallations']['list'][number]

interface StalledCardProps {
  stalled: Installation[]
  onRowClick: (installationId: number) => void
}

const StalledCard = ({ stalled, onRowClick }: StalledCardProps) => {
  const ordered = useMemo(
    () =>
      [...stalled].sort((a, b) =>
        (a.current_stall?.occurred_at ?? '').localeCompare(b.current_stall?.occurred_at ?? '')
      ),
    [stalled]
  )

  return (
    <SectionCard
      title="Needs attention"
      badge={ordered.length === 0 ? 'All clear' : `${ordered.length} stalled`}
      badgeColor={ordered.length === 0 ? 'green' : 'red'}
    >
      <Flex direction="column">
        {ordered.map((row) => {
          const days = row.current_stall ? integerDaysSince(row.current_stall.occurred_at) : 0
          return (
            <Flex
              key={row.installation_id}
              align="center"
              gap="3"
              py="2"
              px="2"
              wrap="wrap"
              className="cursor-pointer rounded-[var(--radius-2)] hover:bg-[var(--gray-a2)]"
              onClick={() => onRowClick(row.installation_id)}
            >
              <Text size="2" className="font-mono">
                #{row.installation_id}
              </Text>
              <Text size="2" weight="medium">
                {row.site?.name ?? row.name ?? '—'}
              </Text>
              <Text size="2" color="gray" className="flex-1 min-w-0 truncate">
                {row.current_stall?.note || row.current_stall?.category || '—'}
              </Text>
              <Badge color={daysStalledColor(days) ?? 'gray'} variant="soft" size="1">
                {days}d
              </Badge>
            </Flex>
          )
        })}
      </Flex>
    </SectionCard>
  )
}

interface OrgOverviewTabProps {
  installations: Installation[]
  stalled: Installation[]
  // Null when Freshdesk isn't configured for this environment; the card is hidden entirely.
  tickets: Ops.Freshdesk.OpenTicket[] | null
  onRowClick: (installationId: number) => void
}

export const OrgOverviewTab = ({ installations, stalled, tickets, onRowClick }: OrgOverviewTabProps) => {
  // Org-wide, unlike the timeline view's panel, which follows whatever the table is showing.
  const health = useMemo(() => computeDeliveryHealth(computeTimingRows(installations)), [installations])
  const siteNameByInstallationId = useMemo(
    () => new Map(installations.map((row) => [String(row.installation_id), row.site?.name ?? row.name ?? '—'])),
    [installations]
  )

  return (
    <Flex direction="column" gap="4">
      <SectionCard title="Delivery performance">
        <DeliveryHealthPanel health={health} />
      </SectionCard>
      <StalledCard stalled={stalled} onRowClick={onRowClick} />
      {tickets && (
        <OrgTicketsCard
          tickets={tickets}
          siteNameByInstallationId={siteNameByInstallationId}
          onInstallationClick={(installationId) => onRowClick(Number(installationId))}
        />
      )}
    </Flex>
  )
}
